import { useId } from 'react';
import { Link } from 'react-router-dom';
import { classNames } from '../lib/format';

/**
 * MeshSmith brand mark.
 * The "M" icon is reproduced from the official asset (SVG/M_Icon_Logo_.svg) as
 * inline vector paths so it stays crisp at any size and needs no extra request.
 * The wordmark is set in Poppins to match the storefront type system.
 */

interface MarkProps {
  className?: string;
  title?: string;
}

export function MeshMark({ className, title = 'MeshSmith' }: MarkProps) {
  const id = useId().replace(/:/g, '');
  return (
    <svg
      viewBox="0 0 62.21 62.21"
      className={className}
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient
          id={`${id}-g`}
          x1="6"
          y1="14"
          x2="56"
          y2="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="var(--logo-from)" />
          <stop offset="1" stopColor="var(--logo-to)" />
        </linearGradient>
      </defs>
      <g fill={`url(#${id}-g)`}>
        <path d="M19.65,32.61l2.17,2.46a2.33,2.33,0,0,0,3.5.34A4.65,4.65,0,0,0,26,34.7q7.79-9.38,15.58-18.76c.58-.71,1.14-1.45,1.78-2.1,2.15-2.18,5.28-1.34,6.48,1.72.92,2.36,1.82,4.73,2.73,7.1q2.49,6.45,5,12.89c1,2.63,2,5.26,3,7.9s-.6,5.76-3,6.11c-.19,0-.38,0-.49,0-.33-1-.63-1.83-1-2.7q-2.67-7.32-5.36-14.62c-.63-1.73-1.26-3.47-1.9-5.2a1.29,1.29,0,0,0-2.31-.51L36.94,38.31c-.94,1.14-1.86,2.3-2.79,3.45a.54.54,0,0,0-.15.3l7.65-7.56.06,0c-.24.39-.47.77-.72,1.15-2.1,3.31-4.16,6.65-6.32,9.91a4.09,4.09,0,0,1-7.16-.09C24.93,41.37,22.37,37.19,19.8,33l-.21-.37Z" />
        <path d="M5,49.56c-1.6,0-3.15-1.49-3.57-3.55a4.6,4.6,0,0,1,.22-2.6c1.84-4.88,3.67-9.75,5.53-14.61q2.45-6.39,4.93-12.74a5.28,5.28,0,0,1,2.22-3,3.74,3.74,0,0,1,4.67,1q4.09,5,8.22,10L30.45,28l.3.38-1.67,1.93c-1,1.17-2,2.33-3,3.51a1.85,1.85,0,0,1-3,.15L18.4,29.3l-2.85-2.83a1.38,1.38,0,0,0-2.38.64l-2.7,7.25L5.93,46.48c-.27.7-.54,1.39-.78,2.1A6.53,6.53,0,0,0,5,49.56Z" />
        <path d="M19.68,33.25c-.22.94-.42,1.83-.64,2.71-.56,2.33-1.12,4.67-1.7,7a2,2,0,0,1-1.09,1.39l-10,4.81s-.09.06-.13.07l-.5.05a2.43,2.43,0,0,1,0-.61q3.55-9.45,7.12-18.88.42-1.11.81-2.22a1,1,0,0,1,2-.3c1.26,1.92,2.61,3.77,3.92,5.65A2.8,2.8,0,0,1,19.68,33.25Z" />
        <path d="M47.59,26.67a1.25,1.25,0,0,1,1.06,1c.64,1.83,1.27,3.67,1.93,5.5C52,37,53.41,41,54.83,44.85c.48,1.31,1,2.62,1.44,3.94a1.85,1.85,0,0,1,.06.42,1.42,1.42,0,0,1-.36-.05l-9.6-4.63-.5-.23a1.82,1.82,0,0,1-1-1.3q-1.11-4.61-2.23-9.21a1,1,0,0,1,.17-.92c1.3-1.87,2.59-3.76,3.87-5.65A1.14,1.14,0,0,1,47.59,26.67Z" />
      </g>
    </svg>
  );
}

interface WordmarkProps {
  className?: string;
  /** Force a light wordmark for use on dark backgrounds. */
  invert?: boolean;
}

export function MeshWordmark({ className, invert }: WordmarkProps) {
  return (
    <span
      className={classNames('font-bold leading-none tracking-tight', className)}
      style={{ color: invert ? '#fff' : 'var(--charcoal)' }}
    >
      Mesh<span style={{ color: invert ? '#fff' : 'var(--charcoal)' }}>Smith</span>
    </span>
  );
}

interface LogoProps {
  to?: string;
  variant?: 'full' | 'stacked' | 'mark';
  className?: string;
  markClassName?: string;
  wordmarkClassName?: string;
  invert?: boolean;
  showTagline?: boolean;
}

export function Logo({
  to = '/',
  variant = 'full',
  className,
  markClassName = 'h-8 w-8',
  wordmarkClassName = 'text-[1.35rem]',
  invert = false,
  showTagline = false,
}: LogoProps) {
  const inner = (
    <span
      className={classNames(
        'inline-flex select-none',
        variant === 'stacked' ? 'flex-col items-center gap-1.5' : 'items-center gap-2',
        className,
      )}
    >
      <MeshMark className={markClassName} />
      {variant !== 'mark' && (
        <span
          className={classNames(
            'flex-col',
            wordmarkClassName,
            variant === 'stacked' && 'items-center',
            // with a tagline the two lines read as one lockup, so drop the extra line height under the name
            showTagline && 'leading-none',
          )}
        >
          <MeshWordmark className="block text-[length:inherit]" invert={invert} />
          {showTagline && (
            <span
              className="mt-1 block text-[0.5rem] font-semibold uppercase tracking-[0.32em]"
              style={{ color: invert ? 'rgba(255,255,255,0.7)' : 'var(--secondary-grey)' }}
            >
              From Mesh to Matter
            </span>
          )}
        </span>
      )}
    </span>
  );

  if (!to) return inner;
  return (
    <Link to={to} aria-label="MeshSmith home" className="inline-flex">
      {inner}
    </Link>
  );
}
